import { MomentInput } from "moment";
import { z } from "zod";
import { IUploadedFile, InspectionActionTypes } from "@miplatform/types";
import {
  addToDate,
  getDate,
  getDateISO,
  requiredString,
} from "@miplatform/utils";

const issueSchema = z.object({
  title: z.string().min(1, "Title is required."),
  description: z.string().min(1, "Description is required."),
  causedByTradeId: z.string().min(1, "Caused by is required."),
  resolvedByTradeId: z.string().min(1, "Resolved by is required."),
  confirmed: z.boolean().refine((val) => val === true, {
    message: "You must confirm the issue.",
  }),
  files: z.array(z.any()).optional(),
});

const base = z.object({
  files: z.array(z.any()).optional(),
});

const scheduleSchema = z.object({
  type: z.literal(InspectionActionTypes.Schedule),
  scheduledDate: requiredString("Schedule date is required."),
});

const rescheduleSchema = z.object({
  type: z.literal(InspectionActionTypes.Reschedule),
  scheduledDate: requiredString("Schedule date is required."),
});

const greenTagSchema = z.object({
  type: z.literal(InspectionActionTypes.GreenTag),
  inspectedDate: requiredString("Inspected date is required."),
  reportedDate: requiredString("Reported date is required."),
});

const redTagSchema = z.object({
  type: z.literal(InspectionActionTypes.RedTag),
  reportedDate: requiredString("Reported date is required."),
  inspectedDate: requiredString("Inspected date is required."),
  issues: z.array(issueSchema),
});

export const inspectionFormSchema = z.discriminatedUnion("type", [
  scheduleSchema.merge(base),
  rescheduleSchema.merge(base),
  greenTagSchema.merge(base),
  redTagSchema.merge(base),
]);

export const getInspectionFormDefaultValues = ({
  scheduledDate,
  type,
}: {
  scheduledDate?: MomentInput;
  type: InspectionActionTypes | null;
}): IInspectionForm => {
  switch (type) {
    case InspectionActionTypes.GreenTag:
      return {
        type,
        reportedDate: getDateISO(),
        inspectedDate: getDateISO(getDate(scheduledDate)),
      };
    case InspectionActionTypes.RedTag:
      return {
        type,
        reportedDate: getDateISO(),
        inspectedDate: getDateISO(getDate(scheduledDate)),
        issues: [
          {
            title: "",
            description: "",
            causedByTradeId: "",
            resolvedByTradeId: "",
            confirmed: false,
          },
        ],
      };
    case InspectionActionTypes.Reschedule:
    case InspectionActionTypes.Schedule:
      return {
        type,
        scheduledDate: getDateISO(addToDate(scheduledDate, 1)),
      };
    default:
      return {} as IInspectionForm;
  }
};

export type IInspectionForm = z.infer<typeof inspectionFormSchema>;

export interface IInspectionFormIssue {
  title: string;
  description: string;
  causedByTradeId: string;
  resolvedByTradeId: string;
  files?: IUploadedFile[];
  confirmed: boolean;
}
